/**
 * Sendungsverfolgung
 *
 * Holt Bestell- und Sendungsdaten beim Tracking-Worker und rendert Status,
 * Positionen und Lieferadresse. Der Worker prueft den Token aus dem Link,
 * fragt DHL ab und cached das Ergebnis -- das Theme kennt weder Bestell-
 * daten noch den DHL-Key.
 *
 * Erwartete Antwort:
 * {
 *   order: {
 *     number, placed_at,
 *     line_items: [{ title, variant_title, quantity, price, compare_at_price, image }],
 *     totals: { subtotal, discount, shipping, total, currency },
 *     address: { name, line1, line2, zip, city, country }
 *   },
 *   shipment: {
 *     carrier, tracking_number, tracking_url, status, status_text, updated_at,
 *     unavailable, events: [{ timestamp, description, location }]
 *   }
 *
 * `unavailable` heisst: der Worker kam gerade nicht an DHL heran (Kontingent
 * leer oder Ausfall) und hat auch keinen alten Stand. Dann wird kein Status
 * behauptet, sondern auf dhl.de verwiesen.
 * }
 */

const STEPS = [
  { key: 'ordered', label: 'Bestellung erhalten' },
  { key: 'handed_over', label: 'An DHL übergeben' },
  { key: 'in_delivery', label: 'In Zustellung' },
  { key: 'delivered', label: 'Zugestellt' },
];

// DHL liefert status.statusCode als pre-transit | transit | delivered | failure | unknown
const STATUS_TO_STEP = {
  ordered: 0,
  'pre-transit': 1,
  pre_transit: 1,
  transit: 2,
  'out-for-delivery': 2,
  out_for_delivery: 2,
  delivered: 3,
};

const FALLBACK_STATUS_TEXT = {
  ordered: 'Bestellung eingegangen',
  'pre-transit': 'Paket ist bei DHL angemeldet',
  pre_transit: 'Paket ist bei DHL angemeldet',
  transit: 'Paket ist unterwegs',
  'out-for-delivery': 'Paket ist in Zustellung',
  out_for_delivery: 'Paket ist in Zustellung',
  delivered: 'Zugestellt',
  failure: 'Es gibt ein Problem mit der Sendung',
  unknown: 'Status noch nicht verfügbar',
};

const CHECK_ICON =
  '<svg viewBox="0 0 16 16" fill="none" aria-hidden="true" focusable="false"><path d="M3 8.5L6.2 11.5L13 4.5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';

const PARCEL_ICON =
  '<svg viewBox="0 0 20 20" fill="none" aria-hidden="true" focusable="false"><path d="M10 2.4 17 6v8l-7 3.6L3 14V6l7-3.6Z" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round"/><path d="M3 6l7 3.6L17 6M10 9.6v8" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round"/></svg>';

const COPY_ICON =
  '<svg viewBox="0 0 16 16" fill="none" aria-hidden="true" focusable="false"><rect x="5.5" y="5.5" width="8" height="8" rx="1.5" stroke="currentColor" stroke-width="1.3"/><path d="M10.5 3.5V3a1.5 1.5 0 0 0-1.5-1.5H3A1.5 1.5 0 0 0 1.5 3v6A1.5 1.5 0 0 0 3 10.5h.5" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/></svg>';

class TrackingStatus extends HTMLElement {
  connectedCallback() {
    this.endpoint = this.dataset.endpoint;
    this.statusEl = this.querySelector('[data-tracking-status]');
    this.orderEl = this.querySelector('[data-tracking-order]');
    this.messageEl = this.querySelector('[data-tracking-message]');

    this.bindOverlays();

    const params = new URLSearchParams(window.location.search);
    this.orderNumber = params.get('o');
    this.token = params.get('t');

    if (!this.orderNumber || !this.token) {
      return this.showMessage(
        'Diesen Link können wir nicht zuordnen. Öffne die Sendungsverfolgung am besten direkt über den Link aus deiner Versandbestätigung.'
      );
    }

    if (!this.endpoint) {
      // Ohne Endpunkt (z.B. im Theme-Editor) bleibt das Skelett stehen.
      return this.showMessage('Für diese Seite ist noch keine Datenquelle hinterlegt.');
    }

    this.load();
  }

  async load() {
    let url;
    try {
      url = new URL(this.endpoint);
    } catch (error) {
      console.warn('Sendungsverfolgung: ungültiger Endpunkt', this.endpoint);
      return this.showMessage('Die Sendungsverfolgung ist gerade nicht erreichbar.');
    }

    url.searchParams.set('o', this.orderNumber);
    url.searchParams.set('t', this.token);

    try {
      const response = await fetch(url.toString(), { headers: { Accept: 'application/json' } });

      if (response.status === 403 || response.status === 404) {
        return this.showMessage(
          'Zu diesem Link finden wir keine Bestellung. Vielleicht ist er abgelaufen — schreib uns kurz, dann schicken wir dir einen neuen.'
        );
      }

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const data = await response.json();
      this.render(data);
    } catch (error) {
      console.warn('Sendungsverfolgung:', error);
      this.showMessage('Die Sendungsverfolgung ist gerade nicht erreichbar. Versuch es in ein paar Minuten nochmal.');
    }
  }

  showMessage(text) {
    if (!this.messageEl) return;
    this.messageEl.textContent = text;
    this.messageEl.hidden = false;
    if (this.statusEl) this.statusEl.hidden = true;
    if (this.orderEl) this.orderEl.hidden = true;
  }

  render(data) {
    const shipment = data.shipment || {};
    const order = data.order || {};

    if (this.messageEl) this.messageEl.hidden = true;
    if (this.statusEl) this.statusEl.hidden = false;
    if (this.orderEl) this.orderEl.hidden = false;

    this.renderStatus(shipment, order);
    this.renderTimeline(shipment);
    this.renderOrder(order, shipment);
    this.announceOrder(order);
  }

  /*
   * Sagt dem Rest der Seite, was bestellt wurde - die Rezept-Section haengt
   * sich daran und sortiert passende Rezepte nach vorn. Ein Event statt eines
   * direkten Zugriffs, damit beide Sections unabhaengig voneinander bleiben
   * und man die Rezepte auch weglassen kann.
   */
  announceOrder(order) {
    const items = Array.isArray(order.line_items) ? order.line_items : [];
    const products = items.map((item) => item.title).filter(Boolean);
    if (!products.length) return;

    document.dispatchEvent(
      new CustomEvent('tracking:order', { detail: { products, number: order.number } })
    );
  }

  /** Fuellt das Verlaufs-Overlay. Neueste Station oben, so kommt sie vom Worker. */
  renderTimeline(shipment) {
    const target = this.querySelector('[data-tracking-timeline]');
    const events = Array.isArray(shipment.events) ? shipment.events : [];
    if (!target || !events.length) return;

    target.innerHTML = events
      .map(
        (event) => `
          <li class="tracking__timeline-item">
            <span class="tracking__timeline-date">${escapeHtml(formatDateTime(event.timestamp))}</span>
            <span class="tracking__timeline-text">${escapeHtml(event.description || '')}</span>
            ${event.location ? `<span class="tracking__timeline-place">${escapeHtml(event.location)}</span>` : ''}
          </li>`
      )
      .join('');
  }

  /*
   * Oeffnen und Schliessen laeuft ueber Delegation am Element selbst: der
   * Status wird per innerHTML neu gebaut, direkt gebundene Listener waeren
   * danach weg.
   */
  bindOverlays() {
    this.addEventListener('click', (event) => {
      const opener = event.target.closest('[data-tracking-open]');
      if (opener) return this.openOverlay(opener.dataset.trackingOpen);

      if (event.target.closest('[data-tracking-close]')) this.closeOverlays();
    });

    this.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') this.closeOverlays();
    });
  }

  openOverlay(name) {
    this.closeOverlays();
    const overlay = this.querySelector(`[data-tracking-overlay="${name}"]`);
    if (!overlay) return;

    overlay.hidden = false;
    overlay.setAttribute('tabindex', '-1');
    overlay.focus();
  }

  closeOverlays() {
    this.querySelectorAll('[data-tracking-overlay]').forEach((overlay) => {
      overlay.hidden = true;
    });
  }

  /*
   * Die Seite beantwortet eine Frage: wann ist das Paket da. Deshalb ist das
   * Zustelldatum die Hauptzeile und der Status nur die Zeile darueber - nicht
   * andersherum. Kennt DHL noch kein Datum, ruecken Status und Text nach.
   */
  renderStatus(shipment, order = {}) {
    if (!this.statusEl) return;

    const status = shipment.status || 'ordered';
    const failed = status === 'failure';
    const unavailable = shipment.unavailable === true;
    const stepIndex = STATUS_TO_STEP[status] ?? 0;
    const delivered = status === 'delivered';

    const parts = ['<div class="tracking__hero">'];

    if (unavailable) {
      // Kein Datum, kein Statuswort: wir wissen zu dieser Sendung gerade nichts
      // und tun auch nicht so. Der Weg zur Antwort steht unten im Kasten.
      parts.push('<p class="tracking__eyebrow">Aktueller Stand</p>');
      parts.push('<p class="tracking__date tracking__date--text">Gerade nicht abrufbar</p>');
    } else if (failed) {
      parts.push('<p class="tracking__eyebrow">Es hakt gerade</p>');
      parts.push('<p class="tracking__date tracking__date--text">Bitte kurz melden</p>');
    } else if (delivered) {
      parts.push('<p class="tracking__eyebrow">Zugestellt</p>');
      parts.push(
        `<p class="tracking__date">${escapeHtml(formatDate(shipment.updated_at) || 'Angekommen')}</p>`
      );
    } else if (shipment.estimated_delivery) {
      parts.push('<p class="tracking__eyebrow">Voraussichtlich bei dir am</p>');
      parts.push(
        `<p class="tracking__date">${escapeHtml(formatDate(shipment.estimated_delivery))}</p>`
      );
    } else {
      parts.push('<p class="tracking__eyebrow">Aktueller Stand</p>');
      parts.push(
        `<p class="tracking__date tracking__date--text">${escapeHtml(STEPS[stepIndex]?.label || 'Unterwegs')}</p>`
      );
    }

    const meta = [];
    if (!failed && !unavailable && (delivered || shipment.estimated_delivery)) {
      meta.push(STEPS[stepIndex]?.label || '');
    }
    if (shipment.updated_at && !delivered) {
      meta.push(`Stand ${formatDateTime(shipment.updated_at)}`);
    }
    if (meta.filter(Boolean).length) {
      parts.push(`<p class="tracking__meta">${escapeHtml(meta.filter(Boolean).join(' · '))}</p>`);
    }

    // Sendungsnummer direkt unter die Metazeile, nicht ans Kartenende: dort
    // haengt der Meldungskasten, und der soll unten allein stehen.
    if (shipment.tracking_number && shipment.tracking_url) {
      parts.push(`
        <p class="tracking__number">
          Sendungsnummer
          <a href="${escapeAttr(shipment.tracking_url)}" target="_blank" rel="noopener">
            ${escapeHtml(shipment.tracking_number)}
          </a>
        </p>
      `);
    }

    parts.push('</div>');

    parts.push(this.renderSteps(failed || unavailable ? -1 : stepIndex));

    // Die Adresse steht hier und nicht in der Bestellkarte: dort wuerde sie die
    // rechte Spalte in die Laenge ziehen, hier fuellt sie den freien Platz
    // zwischen Stationen und Meldungskasten.
    parts.push(this.renderAddress(order.address));

    const events = Array.isArray(shipment.events) ? shipment.events : [];
    const latest = shipment.status_text || FALLBACK_STATUS_TEXT[status];

    if (latest) {
      // Ohne Stand fuehrt die Leiste nach draussen statt in den Verlauf: bei DHL
      // steht die Antwort, und die Sendungsnummer hat der Empfaenger hier ja.
      const more =
        unavailable && shipment.tracking_url
          ? `<a class="tracking__latest-more" href="${escapeAttr(shipment.tracking_url)}"
                target="_blank" rel="noopener">
               Bei DHL nachsehen <span aria-hidden="true">›</span>
             </a>`
          : events.length
            ? `<button type="button" class="tracking__latest-more" data-tracking-open="events">
                 Alle Stationen <span aria-hidden="true">›</span>
               </button>`
            : '';

      parts.push(`
        <div class="tracking__latest">
          <div class="tracking__latest-body">
            <p class="tracking__latest-title">Sendungsstatus</p>
            <p class="tracking__latest-text">${escapeHtml(latest)}</p>
          </div>
          ${more}
        </div>
      `);
    }

    this.statusEl.innerHTML = parts.join('');
  }

  renderAddress(address) {
    if (!address) return '';

    const lines = addressLines(address);
    if (!lines) return '';

    return `
      <div class="tracking__delivery">
        <p class="tracking__delivery-title">Lieferadresse</p>
        <address class="tracking__address">${lines}</address>
      </div>`;
  }

  renderSteps(activeIndex) {
    const items = STEPS.map((step, index) => {
      const isDone = activeIndex >= 0 && index < activeIndex;
      const isCurrent = index === activeIndex;

      let state = '';
      if (isDone) state = ' tracking__step--done';
      if (isCurrent) state = ' tracking__step--current';

      let icon = '';
      if (isDone) icon = CHECK_ICON;
      if (isCurrent) icon = PARCEL_ICON;

      return `
        <li class="tracking__step${state}">
          <span class="tracking__step-dot" title="${escapeAttr(step.label)}">${icon}</span>
        </li>`;
    }).join('');

    return `<ol class="tracking__steps">${items}</ol>`;
  }

  renderOrder(order, shipment) {
    if (!this.orderEl) return;

    const currency = order.totals?.currency || 'EUR';
    const money = (value) => formatMoney(value, currency);
    const parts = [];

    parts.push('<h2 class="tracking__card-title">Deine Bestellung</h2>');

    if (order.number) {
      parts.push(`
        <p class="tracking__order-number">
          <span>Bestellung ${escapeHtml(order.number)}</span>
          <button
            type="button"
            class="tracking__copy"
            data-copy="${escapeAttr(order.number)}"
            aria-label="Bestellnummer kopieren"
          >${COPY_ICON}</button>
        </p>
      `);
    }

    const lineItems = Array.isArray(order.line_items) ? order.line_items : [];
    if (lineItems.length) {
      const items = lineItems
        .map((item) => {
          const image = item.image
            ? `<img src="${escapeAttr(item.image)}" alt="" width="56" height="56" loading="lazy">`
            : '<span></span>';
          const compare =
            item.compare_at_price && item.compare_at_price > item.price
              ? `<span class="tracking__item-compare">${escapeHtml(money(item.compare_at_price))}</span>`
              : '';

          return `
            <li class="tracking__item">
              ${image}
              <span>
                <span class="tracking__item-title">${escapeHtml(item.title || '')}</span>
                ${item.variant_title ? `<span class="tracking__item-meta">${escapeHtml(item.variant_title)}</span>` : ''}
                <span class="tracking__item-meta">${item.quantity || 1}×</span>
              </span>
              <span class="tracking__item-price">
                ${compare}
                ${escapeHtml(money(item.price))}
              </span>
            </li>`;
        })
        .join('');

      parts.push(`<ul class="tracking__items">${items}</ul>`);
    }

    const totals = order.totals;
    if (totals) {
      const rows = [];
      if (totals.subtotal != null) rows.push(row('Zwischensumme', money(totals.subtotal)));
      if (totals.discount) rows.push(row('Rabatt', `−${money(Math.abs(totals.discount))}`, false, 'discount'));
      if (totals.shipping != null) rows.push(row('Versand', totals.shipping ? money(totals.shipping) : 'kostenlos'));
      if (totals.total != null) rows.push(row('Gesamt', money(totals.total), true));

      parts.push(`<div class="tracking__totals">${rows.join('')}</div>`);
    }

    this.orderEl.innerHTML = parts.join('');

    const copyButton = this.orderEl.querySelector('[data-copy]');
    if (copyButton) {
      copyButton.addEventListener('click', () => {
        navigator.clipboard?.writeText(copyButton.dataset.copy);
      });
    }
  }
}

function addressLines(address) {
  return [
    address.name,
    address.line1,
    address.line2,
    [address.zip, address.city].filter(Boolean).join(' '),
    address.country,
  ]
    .filter(Boolean)
    .map((line) => escapeHtml(line))
    .join('<br>');
}

function row(label, value, isSum, modifier) {
  return `
    <div class="tracking__total-row${isSum ? ' tracking__total-row--sum' : ''}${
      modifier ? ` tracking__total-row--${modifier}` : ''
    }">
      <span>${escapeHtml(label)}</span>
      <span>${escapeHtml(value)}</span>
    </div>`;
}

function formatMoney(value, currency) {
  if (value == null) return '';
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency }).format(value / 100);
}

/*
 * Alles fest in deutscher Zeit, nicht in der des Besuchers. Das Paket liegt in
 * Deutschland und die DHL-Seite, mit der Kunden vergleichen, zeigt ebenfalls
 * deutsche Zeit — wer aus dem Urlaub draufguckt, soll dieselbe Uhrzeit sehen
 * wie auf der Sendungsverfolgung von DHL.
 */
const TIME_ZONE = 'Europe/Berlin';

function formatDateTime(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat('de-DE', {
    timeZone: TIME_ZONE,
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

/** Reines Datum, ohne Uhrzeit — für das voraussichtliche Zustelldatum. */
function formatDate(value) {
  if (!value) return '';
  // "2026-08-26" ist ein Kalendertag, kein Zeitpunkt: mittags ansetzen, damit
  // die Umrechnung ihn nicht auf den Vortag schiebt.
  const date = new Date(`${value}T12:00:00Z`);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat('de-DE', {
    timeZone: TIME_ZONE,
    weekday: 'long',
    day: '2-digit',
    month: '2-digit',
  }).format(date);
}

function escapeHtml(value) {
  return String(value ?? '').replace(/[&<>"']/g, (char) => {
    return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[char];
  });
}

function escapeAttr(value) {
  return escapeHtml(value);
}

if (!customElements.get('tracking-status')) {
  customElements.define('tracking-status', TrackingStatus);
}
